import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { DateTime } from 'luxon';
import { Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { GetMoreTransactionsResult, GetTransactionsFilters, TransactionItem, TransactionsStreamEvent } from '../types';

@Injectable({ providedIn: 'root' })
export class TransactionsService {
  private authService = inject(AuthService);
  private httpClient = inject(HttpClient);
  apiHost = `${environment.apiBaseUrl}`;

  streamTransactions(filters: GetTransactionsFilters): Observable<TransactionsStreamEvent> {
    return this.authService.getAccessTokenSilently({ detailedResponse: true }).pipe(
      switchMap((tokenResponse) => {
        return new Observable<TransactionsStreamEvent>((observer) => {
          const url = new URL(`${this.apiHost}/Transactions`, window.location.origin);
          url.searchParams.append('access_token', tokenResponse.access_token);
          url.searchParams.append('from_date', DateTime.fromJSDate(filters.from_date).toFormat('yyyy-MM-dd'));
          url.searchParams.append('to_date', DateTime.fromJSDate(filters.to_date).toFormat('yyyy-MM-dd'));
          filters.location_ids?.forEach((id) => url.searchParams.append('location_ids', String(id)));

          const ev = new EventSource(url.toString());

          const onError = () => {
            observer.complete();
            ev.close();
          };

          const handleSummary = (event: MessageEvent<string>) => {
            try {
              const payload: { total_count: number } = JSON.parse(event.data);
              observer.next({ type: 'summary', totalCount: payload.total_count ?? 0 });
            } catch {
              onError();
            }
          };

          const handleTransaction = (event: MessageEvent<string>) => {
            try {
              const payload: TransactionItem = JSON.parse(event.data);
              observer.next({ type: 'transaction', transaction: payload });
            } catch {
              onError();
            }
          };

          const cleanup = () => {
            ev.removeEventListener('summary', handleSummary);
            ev.removeEventListener('transaction', handleTransaction);
            ev.removeEventListener('error', onError);
            ev.close();
          };

          ev.addEventListener('summary', handleSummary);
          ev.addEventListener('transaction', handleTransaction);
          ev.addEventListener('error', onError);

          return () => cleanup();
        });
      }),
    );
  }

  getMoreTransactions(cancellationUniqueId: string, afterTime: Date): Observable<GetMoreTransactionsResult> {
    const params = new HttpParams().set('after_time', DateTime.fromJSDate(afterTime).toISO({ includeOffset: false })!);
    return this.httpClient.get<GetMoreTransactionsResult>(`${this.apiHost}/Transactions/${cancellationUniqueId}/MoreTransactions`, {
      params,
    });
  }
}
